import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';
import GalleryCard from '@/pages/gallery/components/GalleryCard';
import LightboxModal from '@/pages/gallery/components/LightboxModal';
import { galleryItems, type GalleryItem } from '@/mocks/galleryData';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const PAGE_SIZE = 9;

const galleryJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '갤러리 | 대구드레스룸 키친스',
    description: '대구드레스룸 키친스의 드레스룸, 붙박이장, 팬트리 제품 이미지 갤러리.',
    url: `${SITE_URL}/gallery`,
    inLanguage: 'ko',
    isPartOf: {
      '@type': 'WebSite',
      name: '대구드레스룸 키친스',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: '대구드레스룸 키친스 갤러리',
    description: '드레스룸, 붙박이장, 팬트리, 거실장, 신발장 시공 이미지 모음',
    url: `${SITE_URL}/gallery`,
    author: {
      '@type': 'LocalBusiness',
      name: '대구드레스룸 키친스',
    },
  },
];

export default function GalleryPage() {
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(galleryItems.length / PAGE_SIZE);
  const pagedItems = galleryItems.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleCardClick = (item: GalleryItem) => {
    setSelectedItem(item);
  };

  const handleClose = () => {
    setSelectedItem(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SeoHead
        title="갤러리 | 대구드레스룸 키친스"
        description="대구드레스룸 키친스의 드레스룸, 붙박이장, 팬트리, 거실장, 신발장 제품 이미지를 확인해보세요. 대구 북구 침산동 드레스룸 전문업체."
        keywords="드레스룸갤러리, 붙박이장사진, 팬트리이미지, 대구드레스룸"
        canonical={`${SITE_URL}/gallery`}
        jsonLd={galleryJsonLd}
      />
      <Navbar />

      <main className="flex-1 pt-16">
        {/* Page Header */}
        <section className="border-b border-gray-100 py-8">
          <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1 className="text-xl font-semibold text-gray-800">갤러리</h1>
              <p className="text-sm text-gray-500 mt-1">
                대구드레스룸 키친스의 다양한 제품 이미지를 확인해보세요.
              </p>
            </div>
            <span className="text-sm text-gray-400">
              총 <strong className="text-gray-600">{galleryItems.length}</strong>개
            </span>
          </div>
        </section>

        {/* Grid */}
        <section className="max-w-6xl mx-auto px-6 py-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {pagedItems.map((item, idx) => (
              <GalleryCard
                key={item.id}
                item={item}
                index={(currentPage - 1) * PAGE_SIZE + idx}
                onClick={handleCardClick}
              />
            ))}
          </div>
        </section>

        {/* Pagination */}
        {totalPages > 1 && (
          <section className="max-w-6xl mx-auto px-6 pb-12">
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                aria-label="이전 페이지"
              >
                <i className="ri-arrow-left-s-line" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer ${
                    currentPage === page
                      ? 'bg-yellow-500 text-white'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                aria-label="다음 페이지"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>

            <p className="text-center text-xs text-gray-400 mt-3">
              {currentPage} / {totalPages} 페이지
            </p>
          </section>
        )}
      </main>

      <Footer />

      {/* Lightbox */}
      <LightboxModal item={selectedItem} onClose={handleClose} />
    </div>
  );
}
